import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, MailPlus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { adminApi, errMsg } from "@/lib/api";
import { blankClient } from "@/lib/migrate";

const STATS = [["total_clients", "Total clients"], ["invitations_pending", "Invitations pending"], ["in_progress", "Assessments in progress"], ["new_submissions", "New submissions"], ["reviews_completed", "Reviews completed"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "-");
export const StatusBadge = ({ status }) => <span className={`status-badge st-${status}`} data-testid="status-badge">{(status || "").replace("_", " ")}</span>;

const InviteForm = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await adminApi.invite(f.name.trim(), f.email.trim(), blankClient(f.name.trim()));
      toast[r.email_sent ? "success" : "warning"](r.email_sent ? `Invitation sent to ${f.email}` : "Invitation created, but the email could not be sent. Use Resend.");
      setF({ name: "", email: "" }); onDone();
    } catch (x) { toast.error(errMsg(x)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="panel" data-testid="invite-form">
      <div className="panel-head"><h3>Invite client</h3></div>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <Input data-testid="invite-name-input" placeholder="Client full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required className="h-10 sm:h-9 bg-white" />
        <Input data-testid="invite-email-input" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" placeholder="client@email.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="h-10 sm:h-9 bg-white" />
        <Button type="submit" disabled={busy} data-testid="invite-send-button" className="gap-1.5 h-10 sm:h-9">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailPlus className="h-4 w-4" />} Send invitation</Button>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">The client gets a secure single-use link (valid 7 days), creates a password and starts with a blank assessment.</p>
    </form>
  );
};

const Invitations = ({ list, reload }) => {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (x) { toast.error(errMsg(x)); } };
  const open = list.filter((i) => i.status !== "accepted" && i.status !== "revoked");
  return (
    <section className="panel" data-testid="invitations-panel">
      <div className="panel-head"><h3>Open invitations</h3><span className="text-xs text-slate-400">{open.length}</span></div>
      {!open.length && <p className="text-sm text-slate-400">No open invitations.</p>}
      {open.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-dashed border-slate-100 last:border-0" data-testid={`invitation-row-${i.id}`}>
          <span className="text-sm min-w-0 flex flex-wrap items-center gap-x-1.5"><b>{i.name}</b> <span className="text-slate-500 break-all">{i.email}</span> <StatusBadge status={i.status} /></span>
          <span className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => act(() => adminApi.resend(i.id), "Invitation re-sent")} data-testid={`invitation-resend-${i.id}`} className="gap-1"><RefreshCw className="h-3.5 w-3.5" /> Resend</Button>
            <Button variant="ghost" size="sm" onClick={() => act(() => adminApi.revoke(i.id), "Invitation revoked")} data-testid={`invitation-revoke-${i.id}`}><X className="h-3.5 w-3.5" /></Button>
          </span>
        </div>
      ))}
    </section>
  );
};

const InProgress = ({ list }) => (
  <section className="panel" data-testid="in-progress-panel">
    <div className="panel-head"><h3>Clients working on their assessment</h3><span className="text-xs text-slate-400">{list.length}</span></div>
    {!list.length && <p className="text-sm text-slate-400">No clients in progress.</p>}
    <div className="space-y-2">
      {list.map((c) => (
        <div key={c.id} className="grid grid-cols-1 sm:grid-cols-[1fr_220px_150px] items-center gap-2 py-1.5 border-b border-dashed border-slate-100 last:border-0" data-testid={`in-progress-row-${c.id}`}>
          <span className="text-sm min-w-0 truncate"><Link to={`/clients/${c.id}`} className="font-semibold text-[#0072C6] hover:underline" data-testid={`in-progress-open-${c.id}`}>{c.name}</Link> <span className="text-slate-500">{c.email}</span> {c.resubmit_needed ? <StatusBadge status="submitted" /> : <StatusBadge status={c.status} />}{c.resubmit_needed && <span className="text-[11px] text-amber-700 ml-1">edited after submitting</span>}</span>
          <span className="flex items-center gap-2"><span className="progress-track flex-1 !mt-0"><span className="progress-fill block" style={{ width: `${c.progress}%` }} /></span><span className="font-mono text-xs font-bold text-[#0b2545] w-10 text-right" data-testid={`in-progress-percent-${c.id}`}>{c.progress}%</span></span>
          <span className="text-[11px] text-slate-500 sm:text-right">{c.status === "invited" ? "Not opened yet" : `Updated ${fmtDate(c.updated_at)}`}</span>
        </div>
      ))}
    </div>
  </section>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [inv, setInv] = useState([]);
  const [active, setActive] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const load = () => { adminApi.stats().then(setStats); adminApi.invitations().then(setInv); adminApi.portalClients().then(setActive); };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => adminApi.submissions(q).then(setSubs), 200); return () => clearTimeout(t); }, [q]);
  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4" data-testid="admin-dashboard">
        <div><p className="metric-label">Admin Portal</p><h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0b2545]">Dashboard</h1></div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3">
          {STATS.map(([k, label]) => (
            <div key={k} className="kpi-card"><span className="metric-label">{label}</span><span className="font-mono text-xl sm:text-2xl font-bold text-[#0b2545]" data-testid={`stat-${k}`}>{stats ? stats[k] : "–"}</span></div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-4"><InviteForm onDone={load} /><Invitations list={inv} reload={load} /></div>
        <InProgress list={active} />
        <section className="panel" data-testid="submissions-panel">
          <div className="panel-head flex-wrap gap-2">
            <h3>Submitted financial assessments</h3>
            <div className="relative w-full sm:w-64"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input data-testid="submissions-search-input" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 pl-8 bg-white" /></div>
          </div>
          <div className="sm:hidden space-y-2" data-testid="submissions-cards">
            {subs.map((x) => (
              <button type="button" key={x.id} onClick={() => nav(`/admin/submissions/${x.id}`)} className="sub-card" data-testid={`submission-card-${x.id}`}>
                <span className="flex items-center justify-between gap-2"><b className="text-[#0072C6] truncate">{x.name}</b><StatusBadge status={x.status} /></span>
                <span className="text-xs text-slate-500 truncate">{x.email}</span>
                <span className="text-xs text-slate-600">v{x.version} · {fmtDate(x.submitted_at)}{x.shared_at ? " · report sent" : ""}</span>
              </button>
            ))}
            {!subs.length && <p className="text-slate-400 text-sm py-2">No submissions yet.</p>}
          </div>
          <div className="overflow-x-auto hidden sm:block">
            <table className="data-table" data-testid="submissions-table">
              <thead><tr><th>Client name</th><th>Email</th><th>Submitted</th><th>Version</th><th>Status</th><th /></tr></thead>
              <tbody>
                {subs.map((x) => (
                  <tr key={x.id} data-testid={`submission-row-${x.id}`}>
                    <td><button type="button" className="font-semibold text-[#0072C6] hover:underline" onClick={() => nav(`/admin/submissions/${x.id}`)} data-testid={`submission-open-${x.id}`}>{x.name}</button></td>
                    <td className="text-slate-600">{x.email}</td><td>{fmtDate(x.submitted_at)}</td><td>v{x.version}</td><td><StatusBadge status={x.status} /></td>
                    <td className="text-right"><Link to={`/admin/submissions/${x.id}/report`} className="text-xs font-semibold text-[#0072C6] underline" data-testid={`submission-report-${x.id}`}>View report</Link></td>
                  </tr>
                ))}
                {!subs.length && <tr><td colSpan={6} className="text-slate-400 text-sm py-4">No submissions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}

export { fmtDate };
